import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReviewList = ({ filterStatus, onRespond }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadReviews();
    }
  }, [user, filterStatus]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError('');

      let query = supabase?.from('provider_reviews')?.select('*')?.eq('provider_id', user?.id)?.order('review_date', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        if (fetchError?.code?.substring(0, 2) === '42') {
          console.error('Schema error:', fetchError?.message);
          throw fetchError;
        } else if (fetchError?.code === 'PGRST116') {
          setReviews([]);
          return;
        } else {
          console.log('Data error:', fetchError?.message);
          setError(fetchError?.message);
          return;
        }
      }

      setReviews(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (reviewId, currentFeatured) => {
    try {
      const { error: updateError } = await supabase?.from('provider_reviews')?.update({ is_featured: !currentFeatured, updated_at: new Date()?.toISOString() })?.eq('id', reviewId)?.eq('provider_id', user?.id);

      if (updateError) {
        if (updateError?.code?.substring(0, 2) === '42') {
          console.error('Schema error:', updateError?.message);
          throw updateError;
        } else {
          console.log('Update error:', updateError?.message);
          return;
        }
      }

      loadReviews();
    } catch (err) {
      console.error('Error toggling featured:', err);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            className={star <= rating ? 'text-warning fill-warning' : 'text-muted'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-32 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <p className="text-error">Fout bij laden: {error}</p>
      </div>
    );
  }

  if (reviews?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Geen reviews gevonden</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews?.map((review) => (
        <div
          key={review?.id}
          className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-foreground">
                  {review?.customer_name}
                </h3>
                {review?.is_featured && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                    <Icon name="Star" size={12} className="mr-1" />
                    Uitgelicht
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                {renderStars(review?.rating)}
                <span>•</span>
                <span>{review?.project_type}</span>
                <span>•</span>
                <span>{formatDate(review?.review_date)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-foreground">{review?.review_text}</p>
            </div>

            {review?.response_text && (
              <div className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="MessageCircle" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Uw reactie</span>
                  {review?.response_date && (
                    <span className="text-xs text-muted-foreground">
                      • {formatDate(review?.response_date)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground">{review?.response_text}</p>
              </div>
            )}

            <div className="flex items-center space-x-2 pt-2">
              {review?.status === 'pending' && (
                <Button
                  size="sm"
                  variant="default"
                  iconName="MessageSquare"
                  onClick={() => onRespond(review)}
                >
                  Reageren
                </Button>
              )}
              {review?.status === 'responded' && (
                <Button
                  size="sm"
                  variant="outline"
                  iconName="Edit"
                  onClick={() => onRespond(review)}
                >
                  Reactie Bewerken
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                iconName={review?.is_featured ? 'StarOff' : 'Star'}
                onClick={() => handleToggleFeatured(review?.id, review?.is_featured)}
              >
                {review?.is_featured ? 'Niet Uitlichten' : 'Uitlichten'}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReviewList;